/**
 * downloadSectionFrames — dev-only bulk download of the frames belonging to the
 * section currently open in the morph viewer.
 *
 * The morph iframe URL carries everything we need: `base` points at the section
 * directory (the one holding cameras.json) and `sas` is the blob token. Frames
 * live under `<base>frames/<frame_file>` — same resolution rule as getFrameUrl()
 * in morph-viewer-v2.html.
 *
 * Files are packed into a ZIP with the "stored" method (no compression) — JPEGs
 * are already compressed, so deflating them would only burn CPU. That keeps the
 * writer small enough to live here instead of pulling in a zip dependency.
 */

/** True only when the app is served from the local dev machine. */
export function isLocalHost(): boolean {
  const h = window.location.hostname
  if (h === 'localhost' || h === '127.0.0.1' || h === '::1' || h === '[::1]') return true
  // LAN addresses (phone testing against `vite --host`) count as local too,
  // but only while running the dev server — never in a production build.
  if (!import.meta.env.DEV) return false
  return /^(10\.|127\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(h)
}

/** Pull `base` / `sas` out of a `/morph-viewer/morph-viewer-v2.html?...` URL. */
export function parseMorphUrl(morphUrl: string | null): { base: string; sas: string } | null {
  if (!morphUrl) return null
  const query = morphUrl.split('?')[1]
  if (!query) return null
  const params = new URLSearchParams(query)
  const base = params.get('base')
  if (!base) return null
  return { base, sas: params.get('sas') ?? '' }
}

function appendSas(url: string, sas: string): string {
  if (!sas) return url
  return url + (url.includes('?') ? '&' : '?') + sas
}

/** Mirrors getFrameUrl() in morph-viewer-v2.html. */
function frameUrl(base: string, sas: string, frameFile: string): string {
  if (frameFile.includes('/')) return appendSas(base + frameFile, sas)
  return appendSas(base + 'frames/' + frameFile, sas)
}

// ── CRC32 ─────────────────────────────────────────────────────────────────────

let crcTable: Uint32Array | null = null

function getCrcTable(): Uint32Array {
  if (crcTable) return crcTable
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    table[i] = c >>> 0
  }
  crcTable = table
  return table
}

function crc32(bytes: Uint8Array): number {
  const table = getCrcTable()
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i++) c = table[(c ^ bytes[i]) & 0xff] ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

// ── Minimal ZIP writer (store method) ─────────────────────────────────────────

interface ZipEntry {
  name: string
  crc: number
  size: number
  blob: Blob
}

function dosDateTime(d: Date): { time: number; date: number } {
  const time = (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1)
  const date = ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate()
  return { time, date }
}

function buildZip(entries: ZipEntry[]): Blob {
  const encoder = new TextEncoder()
  const { time, date } = dosDateTime(new Date())
  const parts: BlobPart[] = []
  const central: BlobPart[] = []
  let centralSize = 0
  let offset = 0

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.name)

    const local = new Uint8Array(30 + nameBytes.length)
    const lv = new DataView(local.buffer)
    lv.setUint32(0, 0x04034b50, true)
    lv.setUint16(4, 20, true)      // version needed
    lv.setUint16(6, 0x0800, true)  // UTF-8 names
    lv.setUint16(8, 0, true)       // method: stored
    lv.setUint16(10, time, true)
    lv.setUint16(12, date, true)
    lv.setUint32(14, entry.crc, true)
    lv.setUint32(18, entry.size, true)
    lv.setUint32(22, entry.size, true)
    lv.setUint16(26, nameBytes.length, true)
    lv.setUint16(28, 0, true)      // extra field length
    local.set(nameBytes, 30)
    parts.push(local, entry.blob)

    const cd = new Uint8Array(46 + nameBytes.length)
    const cv = new DataView(cd.buffer)
    cv.setUint32(0, 0x02014b50, true)
    cv.setUint16(4, 20, true)      // version made by
    cv.setUint16(6, 20, true)      // version needed
    cv.setUint16(8, 0x0800, true)
    cv.setUint16(10, 0, true)
    cv.setUint16(12, time, true)
    cv.setUint16(14, date, true)
    cv.setUint32(16, entry.crc, true)
    cv.setUint32(20, entry.size, true)
    cv.setUint32(24, entry.size, true)
    cv.setUint16(28, nameBytes.length, true)
    cv.setUint16(30, 0, true)      // extra
    cv.setUint16(32, 0, true)      // comment
    cv.setUint16(34, 0, true)      // disk number start
    cv.setUint16(36, 0, true)      // internal attrs
    cv.setUint32(38, 0, true)      // external attrs
    cv.setUint32(42, offset, true) // local header offset
    cd.set(nameBytes, 46)
    central.push(cd)
    centralSize += cd.length

    offset += local.length + entry.size
  }

  const eocd = new Uint8Array(22)
  const ev = new DataView(eocd.buffer)
  ev.setUint32(0, 0x06054b50, true)
  ev.setUint16(4, 0, true)
  ev.setUint16(6, 0, true)
  ev.setUint16(8, entries.length, true)
  ev.setUint16(10, entries.length, true)
  ev.setUint32(12, centralSize, true)
  ev.setUint32(16, offset, true)
  ev.setUint16(20, 0, true)

  return new Blob([...parts, ...central, eocd], { type: 'application/zip' })
}

// ── Public API ────────────────────────────────────────────────────────────────

export interface DownloadFramesOptions {
  /** Morph iframe URL currently loaded (source of `base` + `sas`). */
  morphUrl: string | null
  /** Frame file names from the store's cameras; fetched from cameras.json when empty. */
  frameFiles?: string[]
  /** Name of the generated .zip (without extension). */
  fileName?: string
  /** Parallel requests. */
  concurrency?: number
  onProgress?: (done: number, total: number) => void
  signal?: AbortSignal
}

export interface DownloadFramesResult {
  downloaded: number
  failed: number
  bytes: number
}

/**
 * Fetch every frame of the section referenced by `morphUrl` and hand the user a
 * single .zip. Frames that 404 (a known case — see appStore's missingFrames) are
 * skipped rather than failing the whole download.
 */
export async function downloadSectionFrames({
  morphUrl,
  frameFiles,
  fileName = 'frames',
  concurrency = 6,
  onProgress,
  signal,
}: DownloadFramesOptions): Promise<DownloadFramesResult> {
  const parsed = parseMorphUrl(morphUrl)
  if (!parsed) throw new Error('No morph viewer URL — the shop is still loading.')
  const { base, sas } = parsed

  let files = (frameFiles ?? []).filter(Boolean)
  if (files.length === 0) {
    // No cameras in the store yet — read the section's cameras.json directly.
    const res = await fetch(appendSas(base + 'cameras.json', sas), { signal })
    if (!res.ok) throw new Error(`cameras.json returned ${res.status}`)
    const data = await res.json()
    files = (data?.cameras ?? [])
      .map((c: { frame_file?: string }) => c?.frame_file)
      .filter(Boolean)
  }
  if (files.length === 0) throw new Error('This section has no frames to download.')

  const total = files.length
  const entries: (ZipEntry | null)[] = new Array(total).fill(null)
  let done = 0
  let failed = 0
  let bytes = 0
  let cursor = 0

  onProgress?.(0, total)

  const worker = async () => {
    while (cursor < total) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
      const index = cursor++
      const file = files[index]
      try {
        const res = await fetch(frameUrl(base, sas, file), { signal })
        if (!res.ok) throw new Error(String(res.status))
        const buffer = await res.arrayBuffer()
        const view = new Uint8Array(buffer)
        entries[index] = {
          name: file.split('/').pop() || file,
          crc: crc32(view),
          size: view.length,
          blob: new Blob([view]),
        }
        bytes += view.length
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') throw err
        failed++
        console.warn(`[downloadSectionFrames] skipped ${file}:`, err)
      }
      done++
      onProgress?.(done, total)
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, total) }, worker))

  const packed = entries.filter((e): e is ZipEntry => e !== null)
  if (packed.length === 0) throw new Error('Every frame request failed.')

  const zip = buildZip(packed)
  const url = URL.createObjectURL(zip)
  const link = document.createElement('a')
  link.href = url
  link.download = `${fileName}.zip`
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Revoke late so the browser has committed the download.
  setTimeout(() => URL.revokeObjectURL(url), 60_000)

  return { downloaded: packed.length, failed, bytes }
}
